import useEmblaCarousel from 'embla-carousel-react'
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  cn,
} from 'oddle-shop-monorepo.design-system'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import Button from './Button'

interface LightweightCarouselProps {
  children: React.ReactNode
  slidesToShow?: number
  spaceBetween?: number
  showArrows?: boolean
  className?: string
  options?: any // Simplified type for now
  onSlideChange?: (index: number) => void
}

const LightweightCarousel: React.FC<LightweightCarouselProps> = ({
  children,
  slidesToShow = 3,
  spaceBetween = 16,
  showArrows = true,
  className,
  options = {},
  onSlideChange,
}) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    slidesToScroll: 1,
    containScroll: 'trimSnaps',
    ...options,
  })

  const [canScrollPrev, setCanScrollPrev] = useState(false)
  const [canScrollNext, setCanScrollNext] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(0)

  // Use ref to store the latest onSlideChange callback to avoid dependency issues
  const onSlideChangeRef = useRef(onSlideChange)
  onSlideChangeRef.current = onSlideChange

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev()
  }, [emblaApi])

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext()
  }, [emblaApi])

  const onSelect = useCallback(() => {
    if (!emblaApi) return
    setCanScrollPrev(emblaApi.canScrollPrev())
    setCanScrollNext(emblaApi.canScrollNext())
    setSelectedIndex(emblaApi.selectedScrollSnap())
    // Use ref to call the latest callback
    onSlideChangeRef.current?.(emblaApi.selectedScrollSnap())
  }, [emblaApi])

  useEffect(() => {
    if (!emblaApi) return

    // Initial setup
    onSelect()

    // Add event listeners
    emblaApi.on('select', onSelect)
    emblaApi.on('reInit', onSelect)

    return () => {
      emblaApi.off('select', onSelect)
      emblaApi.off('reInit', onSelect)
    }
  }, [emblaApi, onSelect])

  const PrevArrow = () => (
    <div className="bg-background absolute left-0 top-0 z-[1] flex h-full items-center">
      <Button
        className={cn(
          'h-[50px] w-[50px] rounded-full p-3',
          !canScrollPrev && 'pointer-events-none opacity-50'
        )}
        variant="default"
        onClick={scrollPrev}
        disabled={!canScrollPrev}
      >
        <ChevronLeftIcon className="!size-8" />
      </Button>
    </div>
  )

  const NextArrow = () => (
    <div className="bg-background absolute right-0 top-0 z-[1] flex h-full items-center">
      <Button
        className={cn(
          'h-[50px] w-[50px] rounded-full p-3',
          !canScrollNext && 'pointer-events-none opacity-50'
        )}
        variant="default"
        onClick={scrollNext}
        disabled={!canScrollNext}
      >
        <ChevronRightIcon className="!size-8" />
      </Button>
    </div>
  )

  return (
    <div className={cn('relative', className)}>
      <div className="mx-[60px] overflow-hidden" ref={emblaRef}>
        <div className="flex" style={{ gap: `${spaceBetween}px` }}>
          {React.Children.map(children, (child, index) => (
            <div
              key={index}
              style={{
                flex: `0 0 calc(100% / ${slidesToShow} - 30px)`,
              }}
            >
              {child}
            </div>
          ))}
        </div>
      </div>
      {showArrows && (
        <>
          <PrevArrow />
          <NextArrow />
        </>
      )}
    </div>
  )
}

export default LightweightCarousel
